import { FunctionComponent, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useApiCall } from '@/hooks/useApiCall';
import { locationStartBalanceService } from '../../api/location-start-balance/location-start-balance.service';
import { StartBalanceEntryRow, StartBalanceEntry } from './StartBalanceEntryRow';
import { useTranslation } from '@/lib';
import { toast } from 'react-toastify';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { GetAssetTypeDTO, GetStartBalanceDTO, GetLocationDTO } from '../../api/location-start-balance/location-start-balance.contracts';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

export const LocationStartBalanceTab: FunctionComponent = () => {
  const { locationId } = useParams();
  const { t } = useTranslation();

  if (!locationId) {
    return null;
  }

  const locationIdNumber = Number.parseInt(locationId);

  // Fetch available asset types with first transaction dates
  const {
    data: assetTypesData,
    isLoading: isLoadingAssetTypes,
    isError: isErrorAssetTypes,
    fetchData: refetchAssetTypes,
  } = useApiCall<GetAssetTypeDTO[]>(() => locationStartBalanceService.getAssetTypesWithFirstTransactionDate(locationIdNumber));

  // Fetch existing start balances for this location
  const {
    data: locationData,
    isLoading: isLoadingLocation,
    isError: isErrorLocation,
    fetchData: refetchLocation,
  } = useApiCall<GetLocationDTO>(() => locationStartBalanceService.getLocation(locationIdNumber));

  const [entries, setEntries] = useState<StartBalanceEntry[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize entries from existing start balances
  useEffect(() => {
    if (locationData?.startBalances) {
      const existingEntries: StartBalanceEntry[] = locationData.startBalances.map((balance: GetStartBalanceDTO) => ({
        id: uuidv4(),
        assetTypeId: balance.assetType.id,
        amount: balance.amount,
        startDate: dayjs(balance.startDate),
      }));
      setEntries(existingEntries);
    }
  }, [locationData]);

  const handleAddEntry = () => {
    const newEntry: StartBalanceEntry = {
      id: uuidv4(),
      assetTypeId: null,
      amount: 1,
      startDate: dayjs(),
    };
    setEntries([...entries, newEntry]);
  };

  const handleUpdateEntry = (id: string, updatedFields: Partial<StartBalanceEntry>) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              ...updatedFields,
            }
          : entry,
      ),
    );
  };

  const handleDeleteEntry = (id: string) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleSubmit = async () => {
    // Get existing balance asset type IDs to filter them out
    const existingBalanceAssetTypeIds = new Set(locationData?.startBalances?.map((balance) => balance.assetType.id) || []);

    // Only validate and send new entries (not existing ones)
    const newEntriesToSave = entries.filter((entry) => !entry.assetTypeId || !existingBalanceAssetTypeIds.has(entry.assetTypeId));

    // Validate new entries
    const invalidEntries = newEntriesToSave.filter((entry) => !entry.assetTypeId || !entry.startDate || entry.amount < 1);

    if (invalidEntries.length > 0) {
      toast.error(t('please_fill_all_fields'));
      return;
    }

    if (newEntriesToSave.length === 0) {
      toast.info(t('no_new_balances_to_save'));
      return;
    }

    setIsSaving(true);

    try {
      // Create location start balances - only for new entries
      const promises = newEntriesToSave.map((entry) => {
        if (entry.assetTypeId && entry.startDate) {
          return locationStartBalanceService.create({
            locationId: locationIdNumber,
            assetTypeId: entry.assetTypeId,
            startBalance: entry.amount,
            startDate: entry.startDate.toISOString(),
          });
        }
        return Promise.resolve({ isSuccess: false });
      });

      const results = await Promise.all(promises);

      // Check if all requests were successful
      const allSuccessful = results.every((result) => result.isSuccess);

      if (allSuccessful) {
        toast.success(t('balances_saved_successfully'));
        // Refresh the data
        refetchLocation();
        refetchAssetTypes();
      } else {
        toast.error(t('failed_to_save_some_balances'));
      }
    } catch (error) {
      toast.error(t('error_saving_balances'));
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = isLoadingAssetTypes || isLoadingLocation;
  const isError = isErrorAssetTypes || isErrorLocation;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !assetTypesData) {
    return <ErrorLoadingDataAlert />;
  }

  const assetTypes = assetTypesData;

  // Filter out asset types that are already selected
  const selectedAssetTypeIds = new Set(entries.map((e) => e.assetTypeId).filter((id): id is number => id !== null));
  const getAvailableAssetTypes = (currentAssetTypeId: number | null): GetAssetTypeDTO[] => {
    return assetTypes.filter((at: GetAssetTypeDTO) => at.id === currentAssetTypeId || !selectedAssetTypeIds.has(at.id));
  };

  const canAddMore = entries.length < assetTypes.length;

  // Separate existing (from server) and new (user-added) entries
  const existingBalanceAssetTypeIds = new Set(locationData?.startBalances?.map((balance) => balance.assetType.id) || []);
  const existingEntries = entries.filter((entry) => entry.assetTypeId && existingBalanceAssetTypeIds.has(entry.assetTypeId));
  const newEntries = entries.filter((entry) => !entry.assetTypeId || !existingBalanceAssetTypeIds.has(entry.assetTypeId));
  const hasExistingBalances = existingEntries.length > 0;
  const hasNewEntries = newEntries.length > 0;

  return (
    <div className="flex flex-col h-full flex-1 flex-grow gap-4 max-w-[1920px]">
      <div className="flex items-center justify-between w-full">
        <div className="text-md font-bold">{t('location_start_balances')}</div>
      </div>

      <Card variant="outlined" className="w-full xl:w-1/2">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('set_starting_balances')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('set_starting_balances_description')}
          </Typography>

          <Alert severity="info" className="mb-4">
            {t('start_balances_cannot_be_updated')}
          </Alert>

          {entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('no_start_balances_added_yet')}
              </Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={handleAddEntry} className="mt-2" disabled={!canAddMore}>
                {t('add_start_balance')}
              </Button>
            </div>
          ) : (
            <>
              {hasExistingBalances && (
                <>
                  <Typography variant="subtitle2" className="mb-2 mt-4" color="text.secondary">
                    {t('existing_start_balances')}
                  </Typography>
                  <div className="flex flex-col max-h-full overflow-y-auto mb-2">
                    {existingEntries.map((entry) => (
                      <StartBalanceEntryRow
                        key={entry.id}
                        entry={entry}
                        availableAssetTypes={getAvailableAssetTypes(entry.assetTypeId)}
                        onChange={handleUpdateEntry}
                        onDelete={handleDeleteEntry}
                        disabled={true}
                      />
                    ))}
                  </div>
                </>
              )}

              {hasNewEntries && (
                <>
                  {hasExistingBalances && (
                    <Typography variant="subtitle2" className="mb-2" color="text.secondary">
                      {t('new_start_balances')}
                    </Typography>
                  )}
                  <div className="flex flex-col max-h-full overflow-y-auto">
                    {newEntries.map((entry) => (
                      <StartBalanceEntryRow
                        key={entry.id}
                        entry={entry}
                        availableAssetTypes={getAvailableAssetTypes(entry.assetTypeId)}
                        onChange={handleUpdateEntry}
                        onDelete={handleDeleteEntry}
                        disabled={isSaving}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className="flex justify-between items-center mt-2">
                <Button variant="outlined" startIcon={<Add />} onClick={handleAddEntry} disabled={!canAddMore || isSaving}>
                  {t('add_another')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSaving || !hasNewEntries}>
                  {isSaving ? <CircularProgress size={24} /> : t('save_balances')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
